import { DeleteOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, message, Popconfirm, Row } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import cmaApi from "../../../../api/cmaApi";
import InputField from "../../../../components/InputField";
import SelectField from "../../../../components/SelectField";
import TableWrap from "../../../../components/Table";
import TopContent from "../../../../components/TopContent";
import PageWrap from "../../../../layout/PageWrap";
import { appRoutes } from "../../../../routes/routes";
import { messages } from "../../../../utils/messages";

const cmaLeadStatus = [
  { key: 1, value: "Mới / chưa xem" },
  { key: 2, value: "Đã xem" },
  { key: 3, value: "Đang xử lý" },
  { key: 4, value: "Hoàn thành" },
  { key: 5, value: "Hủy / spam" },
];

const statusLabel = (v: number) =>
  cmaLeadStatus.find((s) => s.key === v)?.value ?? v;

const CmaLeadsList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [email, setEmail] = useState<string | undefined>();
  const [name, setName] = useState<string | undefined>();
  const [phone, setPhone] = useState<string | undefined>();
  const [trangthai, setTrangthai] = useState<number | undefined>();

  const { handleSubmit, control } = useForm();

  const { data, isLoading, isFetching }: any = useQuery(
    ["cma_leads", currentPage, limit, email, name, phone, trangthai],
    () => cmaApi.getLeads(currentPage, limit, email, name, phone, trangthai)
  );

  const mutation = useMutation(cmaApi.deleteLead, {
    onSuccess: (res: any) => {
      if (res.statusCode === 200) {
        message.success(messages.deleteSuccess);
        queryClient.invalidateQueries("cma_leads");
        queryClient.invalidateQueries("noti");
      } else {
        message.error(res?.data?.message ?? messages.error);
      }
    },
    onError: (err: any) => {
      message.error(err?.data?.message ?? messages.error);
    },
  });

  const onFilter = (dt: any) => {
    setEmail(dt.email || undefined);
    setName(dt.name || undefined);
    setPhone(dt.phone || undefined);
    setTrangthai(dt.trangthai || undefined);
    setCurrentPage(1);
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      width: "50px",
      dataIndex: "index",
      key: "index",
      align: "center",
    },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Company", dataIndex: "company", key: "company" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "Email", dataIndex: "email", key: "email", width: "12%" },
    { title: "Source", dataIndex: "source", key: "source", width: "10%" },
    {
      title: "Date",
      dataIndex: "ngaytao",
      key: "ngaytao",
      render: (record: string) => <>{moment(record).format("DD/MM/YYYY")}</>,
    },
    {
      title: "Trạng thái",
      dataIndex: "trangthai",
      align: "center",
      key: "trangthai",
      width: "10%",
      render: (v: number) => statusLabel(v),
    },
    {
      title: "Detail",
      key: "detail",
      align: "center",
      width: "5%",
      render: (_, record) => (
        <a
          onClick={() =>
            navigate(`${appRoutes.CMA.LEADS.LIST}/${record?._id}`)
          }
        >
          Xem
        </a>
      ),
    },
    {
      title: "Delete",
      align: "center",
      width: "4%",
      render: (_, record: any) => (
        <Popconfirm
          title="Xác nhận xóa mục này"
          onConfirm={(e: any) => {
            e.preventDefault();
            mutation.mutate(record?._id);
          }}
        >
          <DeleteOutlined style={{ color: "red" }} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <PageWrap
      title="CMA · Form liên hệ"
      content={
        <div className="slider_list">
          <TopContent
            content={
              <Row>
                <Col>
                  <b>CMA · Form liên hệ</b>
                </Col>
              </Row>
            }
          />
          <Card bordered={false} className="register_common">
            <Form className="register_common__form">
              <Row
                style={{ marginBottom: "20px" }}
                align="bottom"
                gutter={[16, 10]}
              >
                <Col lg={4} md={8} sm={24}>
                  <InputField
                    label="Name"
                    name="name"
                    control={control}
                    placeholder="Name"
                  />
                </Col>
                <Col lg={4} md={8} sm={24}>
                  <InputField
                    label="Email"
                    name="email"
                    control={control}
                    placeholder="Email"
                  />
                </Col>
                <Col lg={4} md={8} sm={24}>
                  <InputField
                    label="Phone"
                    name="phone"
                    control={control}
                    placeholder="Phone"
                  />
                </Col>
                <Col lg={4} md={8} sm={24}>
                  <SelectField
                    label="Trạng thái"
                    placeholder="Trạng thái"
                    options={cmaLeadStatus}
                    control={control}
                    name="trangthai"
                    allowClear
                  />
                </Col>
                <Col>
                  <Button
                    htmlType="submit"
                    className="primary_btn"
                    onClick={handleSubmit(onFilter)}
                  >
                    Lọc
                  </Button>
                </Col>
              </Row>
            </Form>
            <TableWrap
              loading={isLoading || isFetching}
              columns={columns}
              isShowTotal
              dataSource={data?.data?.source}
              rowKey="_id"
              bordered
              pagination={{
                pageSize: limit,
                current: currentPage,
                total: data?.data?.totalItems,
                onChange: (page: number, pageSize: number) => {
                  window.scrollTo(0, 0);
                  setCurrentPage(page);
                  setLimit(pageSize);
                },
              }}
              className="hover table-slide"
              hasDefaultColumn={false}
            />
          </Card>
        </div>
      }
    />
  );
};

export default CmaLeadsList;
export { cmaLeadStatus };
